import { BillUpdatedEvent } from '@rematter/frontend-lib-api-client/grpc/finance/v1/bill_events_pb';
import { Col, DiffViewer, FieldDisplay } from '@rematter/ui-web';

import {
  billStatusLabels,
  billTypeLabels,
  paymentMethodLabels,
  paymentTermLabels,
} from '../enumLabels';

import { useTranslations } from '@/finance/i18n/useTranslations';

type BillUpdatedDetailProps = {
  event: BillUpdatedEvent;
};

export const BillUpdatedDetail = ({ event }: BillUpdatedDetailProps) => {
  const t = useTranslations();
  const previous = event.previousBill;
  const current = event.updatedBill;

  if (!previous || !current) {
    return (
      <Col className="@BillUpdatedDetail items-center justify-center p-4 text-neutral/40">
        <span>{t.logs.noDataAvailable}</span>
      </Col>
    );
  }

  const formatAmount = (
    amount: { amount?: string; currency?: number } | undefined,
  ) => {
    if (!amount) return 'N/A';
    const value = Number(amount.amount) / 100;
    return `${amount.currency} ${value.toFixed(2)}`;
  };

  const diffs = [
    {
      field: 'documentNumber',
      label: 'Document Number',
      previous: previous.documentNumber,
      current: current.documentNumber,
    },
    {
      field: 'status',
      label: 'Status',
      previous: billStatusLabels[previous.status],
      current: billStatusLabels[current.status],
    },
    {
      field: 'type',
      label: 'Type',
      previous: billTypeLabels[previous.type],
      current: billTypeLabels[current.type],
    },
    {
      field: 'amount',
      label: 'Amount',
      previous: formatAmount(previous.amount),
      current: formatAmount(current.amount),
    },
    {
      field: 'paymentTerm',
      label: 'Payment Term',
      previous: paymentTermLabels[previous.paymentTerm],
      current: paymentTermLabels[current.paymentTerm],
    },
    {
      field: 'preferredPaymentMethod',
      label: 'Payment Method',
      previous: paymentMethodLabels[previous.preferredPaymentMethod],
      current: paymentMethodLabels[current.preferredPaymentMethod],
    },
    {
      field: 'notes',
      label: 'Notes',
      previous: previous.notes,
      current: current.notes,
    },
  ];

  return (
    <Col className="@BillUpdatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          {t.logs.bill}
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <FieldDisplay
            label="Document Number"
            value={current.documentNumber}
          />
        </div>
      </Col>

      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap1>
        <span className="mb-2 text-xs font-medium text-neutral/60">
          {t.logs.changes}
        </span>
        <DiffViewer
          diffs={diffs}
          previousLabel={t.logs.before}
          currentLabel={t.logs.after}
        />
      </Col>
    </Col>
  );
};
